import { AuresAvatar } from "@aureskonnect/react-ui";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";

import { store } from "@store";

import { FlexboxGrid } from "@components/common/FlexboxGrid";
import { KioskConfigLogoSvgIcon } from "@components/SvgIcons";

import { syncStore } from "../store";

export function Header(): JSX.Element {
    const { isAppSynchronized } = useSnapshot(store);

    const { connectedUser, selectedEntityChild } = useSnapshot(syncStore);

    return (
        <header
            style={{
                background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                borderRadius: "20px 20px 0 0",
                padding: "1.5rem 2rem",
                position: "relative",
                overflow: "hidden",
                boxShadow: "0 4px 20px -2px rgba(0, 0, 0, 0.1)",
                border: "1px solid #374151",
                borderBottom: "none",
            }}
        >
            {/* Background accent */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                        "linear-gradient(90deg, #077dbc 0%, #065a94 50%, #077dbc 100%)",
                }}
            />

            <FlexboxGrid
                className="justify-content-between align-items-center"
                styles={{ height: "100px" }}
            >
                {/* Left section - Logo */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2rem",
                    }}
                >
                    <div
                        style={{
                            padding: "1rem",
                            background: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "16px",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                            backdropFilter: "blur(10px)",
                        }}
                    >
                        <KioskConfigLogoSvgIcon width="200" height="80" />
                    </div>

                    {/* Separator */}
                    <div
                        style={{
                            height: "80px",
                            width: "1px",
                            background:
                                "linear-gradient(to bottom, transparent, #4b5563, transparent)",
                        }}
                    />
                </div>

                {/* Right section - User info and status */}
                <FlexboxGrid gap="40px" className="align-items-center">
                    {/* User Info */}
                    <FlexboxGrid
                        className="flex-column justify-content-center"
                        gap="12px"
                    >
                        {/* Greeting */}
                        <div
                            style={{
                                color: "#ffffff",
                                fontSize: "20px",
                                fontWeight: "600",
                                letterSpacing: "0.5px",
                                fontFamily: "Segoe UI, sans-serif",
                                textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                            }}
                        >
                            {t("Hello")}{" "}
                            <span
                                style={{
                                    color: "#65b6d5",
                                    textTransform: "capitalize",
                                }}
                            >
                                {connectedUser?.firstName as string}
                            </span>
                            !
                        </div>

                        {/* Entity name */}
                        <div
                            style={{
                                color: "#d1d5db",
                                fontSize: "14px",
                                fontWeight: "400",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase",
                                fontFamily: "Segoe UI, sans-serif",
                                opacity: 0.9,
                            }}
                        >
                            {selectedEntityChild?.name}
                        </div>

                        {/* Sync Status */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "8px 16px",
                                background: isAppSynchronized
                                    ? "rgba(34, 197, 94, 0.1)"
                                    : "rgba(239, 68, 68, 0.1)",
                                border: `1px solid ${
                                    isAppSynchronized
                                        ? "rgba(34, 197, 94, 0.3)"
                                        : "rgba(239, 68, 68, 0.3)"
                                }`,
                                borderRadius: "25px",
                                backdropFilter: "blur(10px)",
                            }}
                        >
                            {/* Status dot */}
                            <div
                                style={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    backgroundColor: isAppSynchronized
                                        ? "#22c55e"
                                        : "#ef4444",
                                    border: "1px solid rgba(255, 255, 255, 0.3)",
                                    animation: isAppSynchronized
                                        ? "statusPulse 2s ease-in-out infinite"
                                        : "statusBlink 1s ease-in-out infinite",
                                }}
                            />

                            {/* Status text */}
                            <div
                                style={{
                                    color: "#ffffff",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    letterSpacing: "0.3px",
                                    fontFamily: "Segoe UI, sans-serif",
                                }}
                            >
                                {t(
                                    isAppSynchronized
                                        ? "Your kiosk is synchronized"
                                        : "Your kiosk is not synchronized"
                                )}
                            </div>
                        </div>
                    </FlexboxGrid>

                    {/* Avatar */}
                    <div
                        style={{
                            padding: "4px",
                            background: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "50%",
                            border: "2px solid rgba(101, 182, 213, 0.3)",
                            backdropFilter: "blur(10px)",
                        }}
                    >
                        <AuresAvatar
                            email={connectedUser.email as string}
                            name={connectedUser.firstName as string}
                            size="60px"
                            round={true}
                        />
                    </div>
                </FlexboxGrid>
            </FlexboxGrid>

            {/* CSS Animations */}
            <style>{`
        @keyframes statusPulse {
            0%, 100% { 
                opacity: 1;
                transform: scale(1);
            }
            50% { 
                opacity: 0.7;
                transform: scale(1.1);
            }
        }
        
        @keyframes statusBlink {
            0%, 100% { 
                opacity: 1;
            }
            50% { 
                opacity: 0.3;
            }
        }
    `}</style>
        </header>
    );
}
